import useSWR, { SWRConfiguration } from 'swr';

import { getOrders } from 'api/fetchers/get-orders';

import type { GetOrdersResponseBody, Order, OrdersQuery } from 'models/Order';
import { Meta } from 'models/Meta';

type UseOrders = (fulfillmentStatus: string) => {
    orders?: Order[];
    meta?: Meta;
    loading: boolean;
};

const config: SWRConfiguration = {
    revalidateIfStale: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
};

// Shows shop-wide fulfilled/unfulfilled counts on the settings dashboard, independent of the
// Orders page's own filter/provider selection — so it calls the shared getOrders fetcher directly
// (no provider filter, a throwaway pageSize since only `meta.max` is used) rather than the
// useOrders hook. Called twice — once per fulfillment status — each call gets its own SWR cache
// entry keyed by that status.
//
// The Orders page's provider and filter are now plain component state local to that page and its
// own descendants (see OrdersPage.tsx, useOrders.ts), so — unlike when they lived in a redux slice
// — this dashboard widget (rendered on a different page) can no longer read them to force a
// refetch after e.g. a bulk fulfill on the Orders page. These counts only refresh on mount/remount now.
const useOrdersDashboard: UseOrders = (fulfillmentStatus) => {
    const query: OrdersQuery = {
        status: fulfillmentStatus,
        page: 1,
        pageSize: 1,
    };

    const { data, isValidating } = useSWR<GetOrdersResponseBody>(
        ['orders-dashboard', query],
        ([, query]: [string, OrdersQuery]) => getOrders(query),
        config
    );

    return {
        orders: data?.orders,
        meta: data?.meta,
        loading: isValidating,
    };
};

export default useOrdersDashboard;
